import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LazySection from '@/components/base/LazySection';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { refreshUserFromDB } from '@/lib/customAuth';

interface OrderItem {
  name: string;
  quantity: number;
  points: number;
  spec?: string;
  image?: string;
}

interface Order {
  id: string;
  order_no: string;
  items: OrderItem[];
  total_amount: number;
  status: string;
  created_at: string;
  address?: { name?: string; phone?: string; detail?: string } | null;
  logistics_company?: string;
  tracking_no?: string;
  shipped_at?: string;
}

const statusConfig: Record<string, { color: string; bg: string; label: string; icon: string; desc: string }> = {
  '待发货': { color: 'text-amber-500', bg: 'bg-amber-50', label: '等待发货', icon: 'ri-time-line', desc: '商家正在备货，预计1-2个工作日内发出' },
  '待收货': { color: 'text-sky-500', bg: 'bg-sky-50', label: '配送中', icon: 'ri-truck-line', desc: '包裹正在配送中，请注意查收' },
  '已完成': { color: 'text-emerald-600', bg: 'bg-emerald-50', label: '已完成', icon: 'ri-checkbox-circle-line', desc: '订单已完成，感谢您的兑换' },
};

type ModalType = 'confirm' | 'confirmSuccess' | null;

const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, refresh: refreshAuth } = useAuth();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalType>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!id || !user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id)
      .maybeSingle();
    if (!error && data) setOrder(data as Order);
    setLoading(false);
  }, [id, user]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handleConfirmReceive = async () => {
    if (!order || !user) return;
    setActionLoading(true);

    await supabase.from('orders').update({ status: '已完成' }).eq('id', order.id);

    setOrder((prev) => prev ? { ...prev, status: '已完成' } : prev);
    setActionLoading(false);
    setModal('confirmSuccess');
    await refreshUserFromDB();
    refreshAuth();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 max-w-[480px] mx-auto">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 max-w-[480px] mx-auto">
        <i className="ri-file-unknow-line text-5xl text-gray-300 mb-4" />
        <p className="text-gray-500 mb-6 text-sm">订单不存在</p>
        <button onClick={() => navigate('/orders')} className="bg-emerald-500 text-white px-8 py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap">
          返回订单列表
        </button>
      </div>
    );
  }

  const cfg = statusConfig[order.status] || statusConfig['已完成'];
  const trackingNo = order.tracking_no || (['待收货', '已完成'].includes(order.status) ? `SF${order.order_no.slice(-10)}` : '');
  const logisticsCompany = order.logistics_company || '顺丰速运';
  const stepMap: Record<string, number> = { '待发货': 1, '待收货': 3, '已完成': 5 };
  const currentStep = stepMap[order.status] ?? 1;

  return (
    <div className="min-h-screen bg-gray-50 pb-24 max-w-[480px] mx-auto">
      {/* Header */}
      <LazySection animation="fadeIn" delay={0}>
        <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
          <div className="flex items-center px-4 h-14 gap-3">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-arrow-left-line text-gray-700 text-xl" />
            </button>
            <h1 className="font-bold text-gray-900 text-base flex-1">订单详情</h1>
          </div>
        </header>
      </LazySection>

      {/* Status Banner */}
      <LazySection animation="fadeUp" delay={60}>
        <div className={`${cfg.bg} px-4 py-5`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white flex-shrink-0">
              <i className={`${cfg.icon} ${cfg.color} text-2xl`} />
            </div>
            <div>
              <p className={`font-bold text-lg ${cfg.color}`}>{cfg.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{cfg.desc}</p>
            </div>
          </div>

          {/* 进度条 */}
          <div className="flex items-center">
            {['下单', '备货', '发货', '派送', '完成'].map((step, i) => {
              const done = i < currentStep;
              const active = i === currentStep - 1;
              return (
                <div key={step} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      done ? 'bg-emerald-500 text-white' : active ? 'bg-white border-2 border-emerald-400 text-emerald-500' : 'bg-white border border-gray-200 text-gray-300'
                    }`}>
                      {done ? <i className="ri-check-line text-xs" /> : i + 1}
                    </div>
                    <span className={`text-xs mt-1 whitespace-nowrap ${done || active ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>{step}</span>
                  </div>
                  {i < 4 && <div className={`flex-1 h-0.5 mb-4 mx-1 ${done ? 'bg-emerald-400' : 'bg-gray-200'}`} />}
                </div>
              );
            })}
          </div>
        </div>
      </LazySection>

      <div className="px-4 pt-3 space-y-3">
        {/* Address */}
        <LazySection animation="fadeUp" delay={120}>
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-map-pin-2-line text-emerald-500 text-base" />
              <span className="font-semibold text-gray-900 text-sm">收货地址</span>
            </div>
            {order.address ? (
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-semibold text-gray-900 text-sm">{order.address.name || '—'}</span>
                  <span className="text-gray-500 text-sm">{order.address.phone || '—'}</span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{order.address.detail || '—'}</p>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">暂无收货地址</p>
            )}
          </div>
        </LazySection>

        {/* Products */}
        <LazySection animation="fadeUp" delay={180}>
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-shopping-bag-line text-emerald-500 text-base" />
              <span className="font-semibold text-gray-900 text-sm">兑换商品</span>
            </div>
            <div className="space-y-3">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-gray-300 text-2xl" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 font-medium leading-snug mb-1 line-clamp-2">{item.name}</p>
                    <p className="text-xs text-gray-400 mb-1.5">{item.spec || '默认规格'}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-emerald-600 font-bold text-sm">{(item.points ?? 0).toLocaleString()}</span>
                        <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                      </div>
                      <span className="text-gray-400 text-xs">x{item.quantity}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
              <span className="text-sm text-gray-500">共消耗积分</span>
              <div>
                <span className="text-emerald-600 font-bold text-lg">{order.total_amount.toLocaleString()}</span>
                <span className="text-emerald-600 text-xs ml-0.5">积分</span>
              </div>
            </div>
          </div>
        </LazySection>

        {/* Logistics */}
        <LazySection animation="fadeUp" delay={240}>
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-truck-line text-emerald-500 text-base" />
                <span className="font-semibold text-gray-900 text-sm">物流信息</span>
              </div>
              {trackingNo && (
                <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full">
                  <i className="ri-truck-line text-gray-400 text-xs" />
                  <span className="text-xs text-gray-500">{logisticsCompany}</span>
                  <span className="text-xs text-emerald-600 font-medium">{trackingNo}</span>
                </div>
              )}
            </div>

            {order.status === '待发货' ? (
              <div className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-amber-400 text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">商家正在备货中</p>
                  <p className="text-xs text-gray-400 mt-0.5">预计1-2个工作日内发出</p>
                </div>
              </div>
            ) : trackingNo ? (
              <div className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 bg-sky-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-truck-line text-sky-500 text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">{logisticsCompany}</p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono">{trackingNo}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-truck-line text-gray-300 text-xl" />
                </div>
                <p className="text-sm text-gray-400">暂无物流信息</p>
              </div>
            )}
          </div>
        </LazySection>

        {/* Order Info */}
        <LazySection animation="fadeUp" delay={300}>
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-file-list-3-line text-emerald-500 text-base" />
              <span className="font-semibold text-gray-900 text-sm">订单信息</span>
            </div>
            <div className="space-y-2.5">
              {[
                { label: '订单编号', value: order.order_no },
                { label: '下单时间', value: new Date(order.created_at).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) },
                { label: '兑换方式', value: '积分兑换' },
                { label: '运费', value: '免运费' },
                ...(trackingNo ? [{ label: '快递公司', value: logisticsCompany }, { label: '快递单号', value: trackingNo }] : []),
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{item.label}</span>
                  <span className="text-sm text-gray-700 font-medium text-right max-w-[60%] break-all">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </LazySection>

        {/* Actions */}
        <LazySection animation="fadeUp" delay={360}>
          <div className="bg-white rounded-2xl p-4 space-y-2.5 mb-2">
            {order.status === '待收货' && (
              <button onClick={() => setModal('confirm')} className="w-full bg-emerald-500 text-white py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
                确认收货
              </button>
            )}
            {order.status === '已完成' && (
              <button onClick={() => navigate('/shop')} className="w-full bg-emerald-500 text-white py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
                再次兑换
              </button>
            )}
            <button className="w-full border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer whitespace-nowrap">
              联系客服
            </button>
          </div>
        </LazySection>
      </div>

      {/* 确认收货弹窗 */}
      {modal === 'confirm' && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setModal(null)} />
          <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white rounded-t-2xl z-50 px-5 pt-6 pb-10">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mb-3">
                <i className="ri-checkbox-circle-line text-emerald-500 text-3xl" />
              </div>
              <h3 className="font-bold text-gray-900 text-base mb-1.5">确认收货</h3>
              <p className="text-sm text-gray-500 text-center leading-relaxed">请确认您已收到商品并检查无误<br />确认后订单将完成</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-gray-200 text-gray-600 py-3.5 rounded-full text-sm cursor-pointer whitespace-nowrap">再等等</button>
              <button onClick={handleConfirmReceive} disabled={actionLoading}
                className="flex-1 bg-emerald-500 text-white py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-60">
                {actionLoading ? '处理中...' : '确认收货'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 确认收货成功 */}
      {modal === 'confirmSuccess' && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" />
          <div className="fixed inset-0 flex items-center justify-center z-50 px-8">
            <div className="bg-white rounded-3xl p-8 w-full flex flex-col items-center" style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                <i className="ri-check-line text-emerald-500 text-4xl" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">收货成功！</h3>
              <p className="text-sm text-gray-500 text-center mb-6">感谢您的兑换，希望产品对您有帮助</p>
              <button onClick={() => setModal(null)} className="w-full bg-emerald-500 text-white py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">好的</button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default OrderDetailPage;
